import {Product, User} from '@hconnect/apiclient'
import {useMutation} from '@tanstack/react-query'
import {AxiosError, AxiosResponse} from 'axios'
import {useSnackbar} from 'notistack'

import {api} from '../../../App.store'
import {useTranslation} from '../../../hooks/useTranslation'

type InviteUserToProductsParams = {
  user: User
  products: Product[]
}

const inviteUserToProduct = async (user: User, product: Product) => {
  const response: AxiosResponse = await api.post('/users/invite', {
    user_id: user.id,
    product
  })
  return response.data
}

export const useInviteUserToProducts = (user?: User | null, refetchUser?: () => void) => {
  const {enqueueSnackbar} = useSnackbar()
  const {t} = useTranslation()

  return useMutation({
    mutationFn: async ({user, products}: InviteUserToProductsParams) => {
      const results: PromiseSettledResult<unknown>[] = []
      for (const product of products) {
        try {
          const result = await inviteUserToProduct(user, product)
          results.push({status: 'fulfilled', value: result})
        } catch (error) {
          results.push({status: 'rejected', reason: error})
        }
      }
      return results
    },
    onSuccess: (results, {products}) => {
      refetchUser?.()
      const successfulProducts = products.filter(
        (_, index) => results[index].status === 'fulfilled'
      )
      const failedProducts = products.filter((_, index) => results[index].status === 'rejected')

      if (successfulProducts.length > 0) {
        enqueueSnackbar(
          `${t('createUser.invitationSent', {
            name: user?.name || user?.eMail
          })} ${successfulProducts.join(', ')}`,
          {
            anchorOrigin: {vertical: 'top', horizontal: 'center'},
            variant: 'success'
          }
        )
      }

      if (failedProducts.length > 0) {
        enqueueSnackbar(`Failed to invite to: ${failedProducts.join(', ')}`, {
          anchorOrigin: {vertical: 'top', horizontal: 'center'},
          variant: 'error'
        })
      }
    },
    onError: (e) => {
      const error = e as AxiosError<{detail?: string; title?: string}>
      enqueueSnackbar(
        (error.response?.data?.detail || error.response?.data?.title) ?? error.message,
        {variant: 'error'}
      )
      refetchUser?.()
    },
    retry: false
  })
}
